/**
 * Recurring Events Types
 * Feature A2: Support lunar and solar calendar-based recurring events
 */

export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';
export type CalendarType = 'lunar' | 'solar';

/**
 * Configuration for recurring events
 */
export interface RecurringConfig {
    /** Whether recurring is enabled */
    enabled: boolean;
    /** Frequency of recurrence */
    frequency: RecurrenceFrequency;
    /** Calendar type: 'lunar' for Giỗ, 'solar' for birthdays */
    calendarType: CalendarType;
    /** Original event date (YYYY-MM-DD format) */
    startDate: string;
    /** Optional end date for the recurrence */
    endDate?: string;
    /** Interval: every N periods (default: 1) */
    interval: number;
}

/**
 * A virtual occurrence of a recurring event
 */
export interface RecurringOccurrence {
    /** The note this occurrence belongs to */
    noteId: string;
    /** Date of this specific occurrence (YYYY-MM-DD) */
    occurrenceDate: string;
    /** Solar date converted (for lunar events) */
    solarDate: string;
    /** "Năm thứ X" - which anniversary year (1-indexed) */
    yearNumber: number;
    /** Whether this is a virtual (generated) instance */
    isVirtual: boolean;
}

/**
 * Default recurring config for new events
 */
export const DEFAULT_RECURRING_CONFIG: RecurringConfig = {
    enabled: false,
    frequency: 'yearly',
    calendarType: 'solar',
    startDate: '',
    interval: 1,
};

/**
 * Get default recurring config based on event type
 */
export const getDefaultRecurringByEventType = (
    eventType: string
): Partial<RecurringConfig> => {
    switch (eventType) {
        case 'gio':
            // Giỗ typically uses lunar calendar
            return {
                frequency: 'yearly',
                calendarType: 'lunar',
            };
        case 'sinh_nhat':
        case 'ky_niem':
        case 'su_kien':
            // Birthdays, anniversaries use solar calendar
            return {
                frequency: 'yearly',
                calendarType: 'solar',
            };
        default:
            return {
                frequency: 'yearly',
                calendarType: 'solar',
            };
    }
};

/**
 * Labels for UI display
 */
export const RECURRING_LABELS = {
    frequency: {
        daily: 'Hàng ngày',
        weekly: 'Hàng tuần',
        monthly: 'Hàng tháng',
        yearly: 'Hàng năm',
        custom: 'Tùy chỉnh',
    },
    calendarType: {
        lunar: 'Âm lịch 🌙',
        solar: 'Dương lịch ☀️',
    },
} as const;
